import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip } from "recharts";
import { Chips } from "@/components/app/ChipInput";
import { InfoTip } from "@/components/app/Common";
import { WEIGHT_KEYS } from "@/pages/wizard/jobUtils";

const Panel = ({ title, tip, children, testId, className = "" }) => (
  <section className={`panel space-y-3 p-6 ${className}`} data-testid={testId}>
    <h3 className="flex items-center gap-1.5 text-base font-semibold">{title}{tip && <InfoTip text={tip} />}</h3>
    {children}
  </section>
);

function ProfileRadar({ traits }) {
  if (!traits?.length) return <p className="text-sm text-ink-muted">No ideal profile yet.</p>;
  const data = traits.map((t) => ({ trait: t.trait, min: t.min, max: t.max }));
  return (
    <div className="h-72" data-tour="pt-ideal-profile-radar" data-testid="ideal-profile-radar">
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 200 }}>
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke="#E3DED3" />
          <PolarAngleAxis dataKey="trait" tick={{ fill: "#575B54", fontSize: 11 }} />
          <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
          <Radar name="Upper bound" dataKey="max" stroke="#1F4D3A" fill="#1F4D3A" fillOpacity={0.18} isAnimationActive={false} />
          <Radar name="Lower bound" dataKey="min" stroke="#35594A" fill="#35594A" fillOpacity={0.12} strokeDasharray="4 3" isAnimationActive={false} />
          <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #E3DED3", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function OverviewTab({ job }) {
  const r = job.requirements || {};
  const w = job.scoring_weights || {};
  const stages = [
    ["Resume screening", true],
    ["Personality assessment", job.assessment_config?.personality_enabled],
    ["Work sample", job.work_sample_config?.enabled],
    ["AI interview", job.interview_config?.enabled],
  ];
  return (
    <div className="grid gap-6 lg:grid-cols-3" data-tour="pt-job-overview-tab" data-testid="job-overview-tab">
      <div className="space-y-6 lg:col-span-2">
        <Panel title="Summary" testId="overview-summary">
          <p className="text-sm leading-relaxed text-ink-secondary">{job.summary || "No summary yet."}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink-secondary">
            {job.seniority && <span>Seniority: <span className="text-ink capitalize">{job.seniority}</span></span>}
            <span>Minimum experience: <span className="text-ink">{r.min_years_experience != null ? `${r.min_years_experience} years` : "—"}</span></span>
          </div>
        </Panel>
        <Panel title="Requirements" testId="overview-requirements">
          <p className="text-xs text-ink-muted">Must-have skills</p><Chips values={r.must_have_skills} />
          <p className="text-xs text-ink-muted">Nice-to-have skills</p><Chips values={r.nice_to_have_skills} tone="teal" />
          <div className="grid gap-4 sm:grid-cols-3">
            <div><p className="mb-2 text-xs text-ink-muted">Industries</p><Chips values={r.industries} tone="neutral" /></div>
            <div><p className="mb-2 text-xs text-ink-muted">Education</p><Chips values={r.education} tone="neutral" /></div>
            <div><p className="mb-2 text-xs text-ink-muted">Certifications</p><Chips values={r.certifications} tone="neutral" /></div>
          </div>
        </Panel>
        <Panel title="Responsibilities" testId="overview-responsibilities">
          <ul className="list-disc space-y-1 pl-5 text-sm text-ink-secondary">{(r.responsibilities || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
        </Panel>
        <Panel title="Knockout questions" tip="Yes/no questions asked when someone applies." testId="overview-knockout">
          <ul className="space-y-2 text-sm">{(r.knockout_questions || []).map((q, i) => (
            <li key={i} className="flex justify-between gap-4"><span className="text-ink">{q.question}</span><span className="shrink-0 text-ink-muted">Needs “{q.expected_answer}”</span></li>
          ))}</ul>
        </Panel>
      </div>
      <div className="space-y-6">
        <Panel title="Ideal profile" tip="The trait ranges that suit this role. The shaded area between the bounds is the target." testId="overview-ideal-profile">
          <ProfileRadar traits={job.ideal_profile?.traits} />
        </Panel>
        <Panel title="Interview focus areas" testId="overview-focus-areas"><Chips values={job.interview_config?.focus_areas} tone="teal" /></Panel>
        <Panel title="Stages and weights" tip="How much each stage counts towards the final score." testId="overview-weights">
          <ul className="space-y-2 text-sm">{stages.map(([l, on]) => (
            <li key={l} className="flex justify-between"><span className={on ? "text-ink" : "text-ink-muted line-through"}>{l}</span><span className="text-ink-muted">{on ? "On" : "Off"}</span></li>
          ))}</ul>
          <div className="space-y-1.5 border-t border-line pt-3">
            {WEIGHT_KEYS.map(([k, label]) => (
              <div key={k} className="flex items-center gap-3 text-sm">
                <span className="w-40 text-ink-secondary">{label}</span>
                <span className="h-1.5 flex-1 rounded-full bg-brand-tint"><span className="block h-full rounded-full bg-brand" style={{ width: `${(w[k] || 0) * 100}%` }} /></span>
                <span className="w-10 text-right tabular-nums">{Math.round((w[k] || 0) * 100)}%</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="JD quality" testId="overview-jd-quality">
          <p className="font-display text-3xl font-semibold text-ink">{job.jd_quality?.score ?? "—"}<span className="text-sm text-ink-muted">/100</span></p>
          <p className="text-sm text-ink-secondary">{job.jd_quality?.issues?.length || 0} issue(s) flagged during analysis.</p>
        </Panel>
      </div>
    </div>
  );
}
